import { useCallback, useMemo, useState } from 'react'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandExpandContext,
  CommandGroup,
  CommandInput,
  CommandList,
  CommandSpinner,
} from '@hap/components'

import { useDatastore } from '../datastore'
import { CommandSelectAll } from './CommandSelectAll'
import { BaseCommandListProps } from './types'

interface Tag {
  id: string
  name: string
  meta: Record<string, string>
}

interface TagGroup {
  id: string
  name: string
  tags: Tag[]
}

export function TagsCommandList<T extends boolean = false>({
  placeholder = 'Filter Highlights...',
  selected = [],
  onSelect,
  multiselect,
  contextFilters,
}: Props<T>) {
  const [condensedList, setCondensedList] = useState(true)
  const [query, setQuery] = useState('')
  const { data, isInitialLoading } = useDatastore(FilterCollection.Tags)

  const filteredList = useMemo(() => {
    return condensedList
      ? data?.filter((tag) => {
          if (!contextFilters) return true
          const { channelId } = contextFilters
          if (channelId && '$eq' in channelId)
            return channelId.$eq.some(
              (id) =>
                tag.meta.channelIds.includes(id) || tag.meta.channelIds.length === 0,
            )
          return true
        })
      : data
  }, [data, condensedList, contextFilters])

  const isSearching = query.trim().length > 0

  const visibleItems = useMemo(() => {
    if (!isSearching) return filteredList
    const normalizedQuery = query.toLowerCase()
    return filteredList?.filter((tag) => tag.name.toLowerCase().includes(normalizedQuery))
  }, [filteredList, query, isSearching])

  const handleSelectAll = useCallback(
    (newSelection: string[]) => {
      onSelect(newSelection)
    },
    [onSelect],
  )

  const handleToggleItem = useCallback(
    (id: string) => {
      if (multiselect) {
        // Multi-select: toggle the item
        const newSelection = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id]
        onSelect(newSelection)
      } else {
        // Single-select: replace with just this item
        onSelect([id])
      }
    },
    [multiselect, selected, onSelect],
  )

  // Group visible tags for display
  const groups = useMemo(() => {
    return visibleItems?.reduce<Record<string, TagGroup>>((acc, tag) => {
      const group =
        acc[tag.meta.tagGroupId] ||
        ({
          id: tag.meta.tagGroupId,
          name: tag.meta.tagGroupName,
          tags: [],
        } satisfies TagGroup)
      group.tags.push(tag)
      return { ...acc, [tag.meta.tagGroupId]: group }
    }, {})
  }, [visibleItems])

  const expandedResults = (data?.length || 0) - (filteredList?.length || 0)

  return isInitialLoading ? (
    <CommandSpinner />
  ) : (
    <Command>
      <CommandInput
        placeholder={placeholder}
        autoFocus
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="py-1">
        <CommandGroup>
          <CommandSelectAll
            visibleItems={visibleItems || []}
            selectedIds={selected}
            onSelect={handleSelectAll}
            query={query}
            multiselect={multiselect}
          />
        </CommandGroup>
        {Object.values(groups || {}).map((group) => (
          <CommandGroup heading={group.name} key={group.id}>
            {group.tags.map((tag) => (
              <CommandCheckItem
                key={tag.id}
                value={tag.id}
                multiselect={multiselect}
                keywords={[tag.name]}
                className="group mx-1 flex gap-3"
                selected={selected.includes(tag.id)}
                onSelect={() => handleToggleItem(tag.id)}
              >
                <div
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: tag.meta.color || '#ccc' }}
                />
                {tag.name}
              </CommandCheckItem>
            ))}
          </CommandGroup>
        ))}
        <CommandExpandContext
          availableResults={expandedResults}
          onSelect={() => setCondensedList(false)}
        />
      </CommandList>
    </Command>
  )
}

interface Props<T extends boolean | undefined> extends BaseCommandListProps<T, string> {
  contextFilters?: {
    channelId?: {
      $eq: string[]
    }
  }
}
